/**
 * The settings that apply to every public page, in one place.
 *
 * The switch that makes a plant public lives on the plant, because a gardener
 * thinks in plants. What is the same on every page lives here: the name to
 * show under, whether new plants start public, and which parts of a date
 * their pages and shares show (the day, the year: "Jan 21, 2024", "January
 * 2024", "Jan 21"; owner, 2026-10-14; hidden until the database has the
 * columns, private/sql/2026-10-14-public-date-parts.sql). No cover photo to pick
 * (owner, 2026-10-14: the page shows none; the Gardens card uses the newest
 * public photo). No bio, no
 * per-plant anything: the page has no fields of its own, so there is nothing
 * to administer.
 *
 * Both are choices, so both are kept the moment they are made, with "Saved"
 * beside them. They used to save in silence.
 *
 * The garden page itself comes first, in one box (GardenPageRow): its
 * readable address (grow.gardengameplan.com/@amy), which the backend gives
 * every gardener the first time this screen asks, with Copy and Change.
 *
 * A "View my public garden" link leads the screen, to the garden page (once
 * there is an address).
 *
 * Used by: UserProfile (?s=public).
 *
 * @module PublicPageCard
 */

import React from 'react';
import { Switch } from '@/shared/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/shared/hooks/useAuth';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { updateUserProfile } from '@/shared/api/users';
import { getMyGarden, setMyDateParts, type MyGarden } from '@/shared/api/gardens';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { SocialIcon } from '@/shared/components/SocialIcon';
import { linksOf, type LinkKey } from '@/shared/lib/socialLinks';
import { DateTime, formatDateLong, formatDateParts, fromServerStamp, parseDate, type DateParts } from '@/shared/utils/dateUtils';
import { ProfileScreen } from './ProfileScreen';
import { GardenPageRow } from './GardenPageRow';
import { SettingList, SettingRow } from './profileKit';
import { useSavedTicks } from './useSavedTicks';
import { MY_GARDEN_QUERY_KEY, MY_GARDEN_STALE_MS } from '@/config/tunables/garden';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';

/** The name a public page shows: the display name, or one of the gardener's handles. */
type NameSource = 'display' | LinkKey;

/**
 * Profile > Public pages: the garden page (its link with Copy, and its own
 * name), the name to be shown as, and whether new plants start public.
 */
export const PublicPageScreen: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { mysqlUser, updateMysqlUser } = useAuth();
  const { reactTranslate: t } = useTranslation();
  const { saved, flash } = useSavedTicks();
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const newPlantsId = React.useId();
  const dayId = React.useId();
  const yearId = React.useId();
  const queryClient = useQueryClient();
  // The garden page's link and address, given on first look. Hidden until the database has it.
  const { data: garden } = useQuery({ queryKey: [MY_GARDEN_QUERY_KEY], queryFn: getMyGarden, enabled: open, staleTime: MY_GARDEN_STALE_MS });

  if (!mysqlUser) return null;
  const links = linksOf(mysqlUser.profile_links);
  const source: NameSource = (mysqlUser.public_name_source as NameSource | null | undefined) ?? 'display';
  const newPlantsPublic = Boolean(mysqlUser.new_plants_public);

  /**
   * Keeps a choice the moment it is made, and ticks "Saved" beside it.
   *
   * @param key - Which row's tick.
   * @param patch - The profile fields to change.
   */
  const save = async (key: string, patch: { public_name_source?: NameSource; new_plants_public?: boolean }): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const updated = await updateUserProfile(mysqlUser.user_id, patch);
      // Only what was saved: merged into the account as it is now, never a copy from before the save.
      updateMysqlUser({ ...updated, ...patch });
      flash(key);
    } catch (err) {
      // The server's refusal where it gave one; never axios's "Request failed
      // with status code 500".
      setError(errorMessage(err, t('profile.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  /** Which parts of a date the pages show. */
  const dates = garden?.dates ?? null;
  /** A day last year as the pages would say it now, so the choice shows what it does. */
  const dateExample = dates ? formatDateParts(DateTime.local().minus({ years: 1 }).set({ month: 1, day: 21 }), dates, 'short') : '';

  /**
   * Keeps which parts of a date the pages show, and ticks "Saved".
   *
   * @param parts - Whether to show the day of the month, and the year.
   */
  const saveDates = async (parts: DateParts): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const kept = await setMyDateParts(parts);
      queryClient.setQueryData<MyGarden | null>([MY_GARDEN_QUERY_KEY], (old) => (old ? { ...old, dates: { ...kept, ready: true } } : old));
      flash('dates');
    } catch (err) {
      setError(errorMessage(err, t('profile.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ProfileScreen open={open} title={t('profile.public.title')} onClose={onClose}>
      <p className="text-sm text-muted-foreground">{t('profile.public.description')}</p>
      {garden?.slug && (
        <Link to={`/@${garden.slug}`} className="inline-flex min-h-[44px] items-center text-sm font-medium text-primary underline-offset-4 hover:underline">
          {t('profile.public.view')}
        </Link>
      )}
      <SettingList>
        {garden && <GardenPageRow garden={garden} saved={saved('address')} onSaved={() => flash('address')} />}
        <SettingRow title={t('profile.public.nameSource')} hint={t('profile.public.nameSourceHint')} saved={saved('source')} stacked>
          <Select value={source} onValueChange={(value) => void save('source', { public_name_source: value as NameSource })} disabled={saving}>
            <SelectTrigger className="w-full" aria-label={t('profile.public.nameSource')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="display">{mysqlUser.display_name || t('profile.public.displayName')}</SelectItem>
              {links.map((link) => (
                <SelectItem key={link.key} value={link.key}>
                  <span className="flex items-center gap-2">
                    <SocialIcon name={link.key} size={14} />
                    {link.handle}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SettingRow>
        <SettingRow title={t('profile.public.newPlants')} hint={t('profile.public.newPlantsHint')} saved={saved('new')} htmlFor={newPlantsId}>
          <Switch id={newPlantsId} checked={newPlantsPublic} disabled={saving} onCheckedChange={(on) => void save('new', { new_plants_public: on })} />
        </SettingRow>
        {/* Which parts of a date the pages and shares show; the month is always said. */}
        {dates && (
          <SettingRow title={t('profile.public.dates')} hint={t('profile.public.datesHint', { example: dateExample })} saved={saved('dates')} stacked>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <label htmlFor={dayId} className="flex min-h-11 items-center gap-2 text-sm">
                <Switch id={dayId} checked={dates.day} disabled={saving} onCheckedChange={(on) => void saveDates({ day: on, year: dates.year })} />
                {t('profile.public.dateDay')}
              </label>
              <label htmlFor={yearId} className="flex min-h-11 items-center gap-2 text-sm">
                <Switch id={yearId} checked={dates.year} disabled={saving} onCheckedChange={(on) => void saveDates({ day: dates.day, year: on })} />
                {t('profile.public.dateYear')}
              </label>
            </div>
          </SettingRow>
        )}
      </SettingList>
      {error && <p className="text-sm text-destructive"><FailureText text={error} /></p>}
      <p className="text-xs text-muted-foreground">
        {mysqlUser.public_consent_at
          ? t('profile.public.consented', { date: formatDateLong(fromServerStamp(mysqlUser.public_consent_at) ?? parseDate(mysqlUser.public_consent_at)) })
          : t('profile.public.notYet')}
      </p>
    </ProfileScreen>
  );
};
